import { useEffect, useState } from "react";
import ToastContainer from "@/components/ui/ToastContainer";
import { io } from "socket.io-client";
import { Check, AlertTriangle, Package } from "lucide-react";
import { X, Tag, Trash2 } from "lucide-react";
import { useTranslation } from "@/contexts/TranslationContext";
import { useCurrency } from "@/contexts/CurrencyContext";
import AlertBlock from "@/components/ui/AlertBlock";
import BoxBlock from "@/components/ui/BoxBlock";
import Layout from "@/components/ui/Layout";
import { withI18nStaticProps } from "@/lib/withI18nStaticProps"

let socket = null;

import Link from "next/link";

function CartLoading() {
    return (
        <div className="rounded-2xl bg-white dark:bg-zinc-900 shadow-md dark:border dark:border-zinc-700 p-6">
            <div className="flex items-center gap-3">
                <div className="w-5 h-5 rounded-full border-2 border-gray-300 border-t-transparent animate-spin" />
                <div className="text-gray-700 dark:text-gray-300 font-medium">
                    Betöltés...
                </div>
            </div>
        </div>
    );
}

function EmptyCart({ t }) {
    return (
        <BoxBlock
            title={t("cart.empty.title") ?? "A kosarad üres"}
            icon={<Package className="w-5 h-5 text-gray-500 dark:text-gray-300" />}
        >
            <p className="text-gray-700 dark:text-gray-300">
                {t("cart.empty.description") ?? "Még nem tettél terméket a kosárba."}
            </p>

            <Link
                href="/"
                className="
          mt-4 inline-block w-full text-center
          bg-black text-white dark:bg-white dark:text-black
          py-3 rounded-xl font-semibold
          shadow-md active:scale-95 transition cursor-pointer
        "
            >
                {t("cart.empty.cta") ?? "Vásárlás folytatása"}
            </Link>
        </BoxBlock>
    );
}

function formatRemainingTime(endDate, now) {
    const end = new Date(endDate).getTime();
    if (Number.isNaN(end)) return null;

    const diff = end - now;
    if (diff <= 0) return null;

    const hours = Math.floor(diff / 3600000);
    const minutes = Math.floor((diff % 3600000) / 60000);
    const seconds = Math.floor((diff % 60000) / 1000);

    if (hours > 0) {
        return `${hours} óra ${minutes} perc ${seconds} mp`;
    }

    if (minutes > 0) {
        return `${minutes} perc ${seconds} mp`;
    }

    return `${seconds} mp`;
}

function CartPage() {

    const [now, setNow] = useState(Date.now());

    useEffect(() => {
        const interval = setInterval(() => {
            setNow(Date.now());
        }, 1000); // másodpercenként

        return () => clearInterval(interval);
    }, []);


    const { t } = useTranslation();
    const [toasts, setToasts] = useState([]);
    const pushToast = (type, title, message) => {
        const id = `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

        setToasts((prev) => [...prev, { id, type, title, message }]);

        setTimeout(() => {
            setToasts((prev) => prev.filter((t) => t.id !== id));
        }, 3000);
    };

    const handleBackendToasts = (json) => {
        if (Array.isArray(json.toasts)) {
            for (const tObj of json.toasts) {
                pushToast(
                    tObj.type,
                    t(`toast.${tObj.type}`),
                    t(tObj.translationKey, tObj.params)
                );
            }
        } else if (json.toast) {
            // régi API-k miatt még támogatjuk
            pushToast(json.toast.type, json.toast.title, json.toast.message);
        }
    };

    const [cart, setCart] = useState(null);

    const orderLocked = !!cart?.order?.id;

    const [loading, setLoading] = useState(true);

    const [couponCode, setCouponCode] = useState("");


    const [hoverDetach, setHoverDetach] = useState(false);

    const [showAllCouponVariants, setShowAllCouponVariants] = useState(false);

    const couponMismatch = (() => {
        if (!cart?.coupon || !Array.isArray(cart?.cart_items)) return false;

        const allowedVariantIds = cart.coupon.product_variants?.map(pv => pv.id) || [];
        if (allowedVariantIds.length === 0) return false;

        const cartVariantIds = cart.cart_items.map(ci => ci.product_variant.id);

        const hasMatch = cartVariantIds.some(vid => allowedVariantIds.includes(vid));

        return !hasMatch;
    })();

    const status = cart?.order?.order_status;

    const PAY_CTA_BY_STATUS = {
        pending: { href: "/pay", labelKey: "cart.pay.cta.pending" },
        bank_pending: { href: "/pay", labelKey: "cart.pay.cta.bank_pending" },
        otp_szep_pending: { href: "/pay", labelKey: "cart.pay.cta.otp_szep_pending" },
        stripe_pending: { href: "/pay", labelKey: "cart.pay.cta.stripe_pending" },
    };

    const payCta = status ? PAY_CTA_BY_STATUS[status] : null;

    const { currency, formatCurrency } = useCurrency();

    // 🔌 SOCKET csak egyszer
    useEffect(() => {
        if (!socket) {
            socket = io("https://socket.kulturklub.eu", {
                withCredentials: true,
                transports: ["websocket"],
            });
        }
    }, []);

    // 🛒 CART BETÖLTÉS
    const loadCart = async () => {
        setLoading(true);
        try {
            const res = await fetch(`/api/cart/me?t=${Date.now()}`, {
                method: "GET",
                credentials: "include",
                cache: "no-store",
                headers: {
                    "Cache-Control": "no-cache",
                    "Pragma": "no-cache",
                },
            });

            if (!res.ok) {
                console.warn("Cart load failed status:", res.status);
                setCart(null);
                return;
            }

            const json = await res.json();
            setCart(json.data);
        } catch (e) {
            console.error("Cart load failed", e);
        } finally {
            setLoading(false);
        }
    };


    // 🔁 CART LOAD on mount
    useEffect(() => {
        loadCart();
    }, []);

    // 🏠 SOCKET ROOM JOIN — ha megjött a cart
    useEffect(() => {
        if (!cart?.id) return;
        if (!socket) return;

        const room = `cart_${cart.id}`;
        console.log("🟢 Joining cart room:", room);

        socket.emit("room:join", room);

        // CLEANUP
        return () => {
            if (socket) {
                socket.emit("room:leave", room);
            }
        };
    }, [cart?.documentId]);

    useEffect(() => {
        if (!socket) return;
        if (!cart?.id) return;

        function handleCartUpdate(payload) {
            console.log("📦 cart:update received:", payload);

            // 👉 Mivel a cart roomban csak én vagyok, ez MINDIG az én update-em
            setCart(payload.cart);

            // 👉 Toast: tájékoztatjuk a usert, hogy frissült
            if (payload.socketId !== socket.id) {
                pushToast(
                    "info",
                    t("toast.info"),
                    t("cart.toast.updated")
                );
            }
        }

        socket.on("cart:update", handleCartUpdate);

        return () => {
            socket.off("cart:update", handleCartUpdate);
        };
    }, [cart?.id]);

    const applyCoupon = async () => {
        try {
            const res = await fetch("/api/coupon/apply", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    coupon: couponCode,
                    socketId: socket?.id
                })
            });

            const json = await res.json();

            handleBackendToasts(json);

            if (json.cart) {
                setCart(json.cart);
            }

        } catch (err) {
            pushToast("error", "Hiba", "Nem sikerült kapcsolatba lépni a szerverünkkel.");
        }
    };


    const handleRemoveItem = async (item) => {

        if (orderLocked) {
            pushToast("error", t("toast.error"), t("cart.locked.no_changes"));
            return;
        }

        try {
            const payloadItem = {
                product: item.product.id,
                product_variant: item.product_variant.id,
            };

            if (item.seat) {
                payloadItem.seat = item.seat.id;
                payloadItem.quantity = 1;
            } else {
                payloadItem.quantity = 0;
            }

            const res = await fetch("/api/cart/bulk-add", {
                method: "POST",
                body: JSON.stringify({
                    items: [payloadItem],
                    socketId: socket?.id
                })
            });

            const data = await res.json();

            // 🔥 backend toastok
            handleBackendToasts(data);

            // 🔥 ha backend adott cart-ot → frissítsük
            if (data.cart) {
                setCart(data.cart);
            }

        } catch (err) {
            pushToast("error", "Hiba", "Nem sikerült kapcsolatba lépni a szerverünkkel.");
        }
    };


    const detachCoupon = async () => {
        try {
            const res = await fetch("/api/coupon/detach", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ socketId: socket?.id })
            });

            const json = await res.json();

            handleBackendToasts(json);

            if (json.cart) {
                setCart(json.cart);
            }

        } catch (err) {
            pushToast("error", "Hiba", "Nem sikerült kapcsolatba lépni a szerverünkkel.");
        }
    };


    const incrementItem = async (item) => {

        if (orderLocked) {
            pushToast("error", t("toast.error"), t("cart.locked.no_changes"));
            return;
        }

        try {
            const res = await fetch("/api/cart/bulk-add", {
                method: "POST",
                body: JSON.stringify({
                    items: [
                        {
                            product: item.product.id,
                            product_variant: item.product_variant.id,
                            quantity: 1
                        }
                    ],
                    socketId: socket?.id
                })
            });

            const json = await res.json();

            handleBackendToasts(json);

            if (json.cart) setCart(json.cart);
        } catch (err) {
            pushToast("error", "Hiba", "Nem sikerült kapcsolatba lépni a szerverünkkel.");
        }
    };


    const decrementItem = async (item) => {

        if (orderLocked) {
            pushToast("error", t("toast.error"), t("cart.locked.no_changes"));
            return;
        }

        try {
            const res = await fetch("/api/cart/bulk-add", {
                method: "POST",
                body: JSON.stringify({
                    items: [
                        {
                            product: item.product.id,
                            product_variant: item.product_variant.id,
                            quantity: -1
                        }
                    ],
                    socketId: socket?.id
                })
            });

            const json = await res.json();
            handleBackendToasts(json);

            if (json.cart) setCart(json.cart);
        } catch (err) {
            pushToast("error", "Hiba", "Nem sikerült kapcsolatba lépni a szerverünkkel.");
        }
    };

    const groupedCouponVariants = Array.isArray(cart?.coupon?.product_variants)
        ? cart.coupon.product_variants.reduce((acc, pv) => {
            const productId = pv.product?.id || "unknown";

            if (!acc[productId]) {
                acc[productId] = {
                    productId,
                    productName: pv.product?.name || "Ismeretlen termék",
                    productSlug: pv.product?.slug || null,
                    variants: [],
                };
            }

            acc[productId].variants.push({
                id: pv.id,
                name: pv.name,
            });

            return acc;
        }, {})
        : {};

    const groupedCouponVariantList = Object.values(groupedCouponVariants);

    const centerColumn = (
        <>
            {orderLocked && (
                <AlertBlock type="error">
                    A kosaradat át tudod tekinteni, de már nem módosítható, mert lezárásra került.
                </AlertBlock>
            )}

            {orderLocked && (
                <AlertBlock type="info">
                    <div className="flex flex-col w-full">
                        <p>
                            A kosár megrendelt állapotban van, így ez a kosár már nem módosítható. De bármikor úgy dönthetsz, hogy új kosarat hozol létre és abba helyezed a kívánt termékeket. Ehhez csak menj bármelyik termék oldalára és kattints a "Kosárba" gombra.
                            <br />
                            <span className="font-semibold">
                                Státusz: {t(`order.status.${cart?.order?.order_status}`)}
                                <br />
                                Rendelési azonosító: {cart?.order?.order_number}
                            </span>
                            <br />
                            <br />
                            Ha kérdésed van vedd fel velünk a kapcsolatot.
                        </p>

                        {payCta && (
                            <Link
                                href={payCta.href}
                                className="
      block w-full text-center 
      bg-black text-white dark:bg-white dark:text-black 
      py-3 mt-3 rounded-xl font-semibold 
      shadow-md active:scale-95 transition cursor-pointer
    "
                            >
                                {t(payCta.labelKey)}
                            </Link>
                        )}
                    </div>
                </AlertBlock>
            )}

            {/* LOADING */}
            {loading && <CartLoading />}

            {/* EMPTY CART */}
            {!loading && (!Array.isArray(cart?.cart_items) || cart.cart_items.length === 0) && <EmptyCart t={t} />}

            {/* PRODUCTS LIST */}
            {cart?.cart_items?.length > 0 && (
                <div className="space-y-4">
                    {cart.cart_items.map((item) => {
                        const summaryItem = cart?.summary?.items?.find(s => s.cart_item_id === item.id);
                        if (!summaryItem) return null;

                        const product = item.product;
                        const variant = item.product_variant;
                        const gifts = variant?.gift_product_variants || [];
                        const giftQty = item.quantity;

                        // 1) Variant image (elsődleges)
                        let displayImage = variant?.images?.[0]?.url || null;

                        // 2) Ha nincs → product image
                        if (!displayImage && product?.images?.[0]?.url) {
                            displayImage = product.images[0].url;
                        }

                        const isSeatmap = variant.product_variant_attributes?.some(
                            (attr) =>
                                attr.attribute?.key === "is_seatmap" &&
                                attr.custom_value_text === "true"
                        );

                        const currentPrice = summaryItem.base_row_final;
                        const originalPrice = summaryItem.base_row_amount;
                        const isDiscountedFrontend = originalPrice > currentPrice;

                        return (
                            <div
                                key={item.id}
                                className="space-y-4"
                            >

                                <div
                                    className="
  grid 
  grid-cols-[80px_1fr_20px]
  items-start gap-x-4 p-3 
  bg-gray-50 dark:bg-zinc-800 
  rounded-xl shadow-sm
"
                                >
                                    <>
                                        {/* 1: IMAGE */}
                                        <div className="w-20 h-20 rounded-lg overflow-hidden bg-white">
                                            {displayImage ? (
                                                <img
                                                    src={displayImage}
                                                    alt={product.name}
                                                    className="w-full h-full object-cover"
                                                />
                                            ) : (
                                                <div className="w-full h-full bg-gray-200" />
                                            )}
                                        </div>

                                        {/* 2: MIDDLE CONTENT */}
                                        <div className="flex flex-col justify-start min-w-0">

                                            {/* 1️⃣ PRODUCT NAME — első sor */}
                                            <Link
                                                href={`/${product.slug}`}
                                                className="font-semibold text-gray-900 dark:text-gray-100 truncate min-w-0 hover:underline"
                                            >
                                                {product.name}
                                            </Link>

                                            {/* 2️⃣ MÁSODIK SOR: variant + qty + PRICE */}
                                            <div className="flex items-center justify-between mt-1 w-full min-w-0">

                                                {/* Variant + qty */}
                                                <div className="truncate text-sm text-gray-600 dark:text-gray-300 min-w-0">
                                                    {variant.name}
                                                </div>

                                                {/* CURRENT PRICE (jobb oldal) */}
                                                <div className="text-right font-semibold text-lg whitespace-nowrap">
                                                    {formatCurrency(currentPrice)} {currency.symbol}
                                                </div>
                                            </div>

                                            {/* 3️⃣ Áthúzott ár — akció vagy kupon esetén */}
                                            {isDiscountedFrontend && (
                                                <div className="text-right">
                                                    <span className="text-sm text-gray-500 dark:text-gray-400 line-through whitespace-nowrap">
                                                        {formatCurrency(originalPrice)} {currency.symbol}
                                                    </span>
                                                </div>
                                            )}
                                        </div>


                                        {/* 3: DELETE BUTTON */}
                                        <div className="flex items-start justify-center">
                                            <button
                                                onClick={() => handleRemoveItem(item)}
                                                className="
                w-7 h-20 rounded-lg flex items-center justify-center
                bg-black/5 text-black/50 
                hover:bg-black/10 
                dark:bg-white/10 dark:text-white/60
                dark:hover:bg-white/20
                transition cursor-pointer
            "
                                            >
                                                <X size={19} strokeWidth={2.4} />
                                            </button>
                                        </div>

                                        {/* 4️⃣ EXTRA SOR – KÉP ALÓL INDUL */}
                                        <div className="col-span-3 flex items-center justify-between">
                                            <div>
                                                {summaryItem.original_amount > summaryItem.base_amount ? (
                                                    <div className="mt-2">
                                                        <span className="text-sm text-gray-500 dark:text-gray-400">
                                                            A termék legalacsonyabb ára, az elmúlt 30 napban:{" "}
                                                            {formatCurrency(summaryItem.original_amount)} {currency.symbol}
                                                        </span>
                                                    </div>
                                                ) : (
                                                    isSeatmap && (
                                                        <div className="mt-2">
                                                            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                                                {item?.seat?.name}
                                                            </span>
                                                        </div>
                                                    )
                                                )}
                                            </div>

                                            {summaryItem.original_amount > summaryItem.base_amount && isSeatmap && (
                                                <div className="flex items-center mt-4">
                                                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                                        {item?.seat?.name}
                                                    </span>
                                                </div>
                                            )}

                                            {!isSeatmap && (
                                                <div className="flex items-center gap-2 mt-4">
                                                    <button
                                                        onClick={() => decrementItem(item)}
                                                        className="w-7 h-7 flex items-center justify-center rounded-lg 
                   bg-black/5 dark:bg-white/10 hover:bg-black/10 
                   dark:hover:bg-white/20 transition cursor-pointer"
                                                    >
                                                        <span className="text-lg font-bold">−</span>
                                                    </button>

                                                    {/* CURRENT QTY */}
                                                    <span className="text-md font-medium min-w-[24px] text-center">
                                                        {item.quantity}
                                                    </span>

                                                    {/* + BUTTON */}
                                                    <button
                                                        onClick={() => incrementItem(item)}
                                                        className="w-7 h-7 flex items-center justify-center rounded-lg 
                   bg-black/5 dark:bg-white/10 hover:bg-black/10 
                   dark:hover:bg-white/20 transition cursor-pointer"
                                                    >
                                                        <span className="text-lg font-bold">+</span>
                                                    </button>
                                                </div>
                                            )}


                                        </div>
                                    </>
                                </div>

                                {gifts.map((gift) => {

                                    const remaining = !orderLocked && variant?.gift_end_at
                                        ? formatRemainingTime(variant.gift_end_at, now)
                                        : null;

                                    let giftImage = gift?.images?.[0]?.url || null;

                                    if (!giftImage && product?.images?.[0]?.url) {
                                        giftImage = product.images[0].url;
                                    }

                                    const eventAttr = gift.product_variant_attributes?.find(
                                        (a) => a.attribute?.key === "event_start_time"
                                    );

                                    const eventStart = eventAttr?.custom_value_date
                                        ? new Date(eventAttr.custom_value_date)
                                        : null;

                                    const eventDateFormatted = eventStart
                                        ? eventStart.toLocaleDateString("hu-HU", {
                                            year: "numeric",
                                            month: "long",
                                            day: "numeric",
                                        })
                                        : null;

                                    return (
                                        <div
                                            key={`gift_${item.id}_${gift.id}`}
                                            className="
                  grid 
  grid-cols-[80px_1fr_20px]
  items-start gap-x-4 p-3 
  bg-gray-50 dark:bg-zinc-800 
  rounded-xl shadow-sm
            "
                                        >
                                            {/* IMAGE */}
                                            <div className="w-20 h-20 rounded-lg overflow-hidden bg-white">
                                                {giftImage ? (
                                                    <img
                                                        src={giftImage}
                                                        alt={gift.name}
                                                        className="w-full h-full object-cover"
                                                    />
                                                ) : (
                                                    <div className="w-full h-full bg-gray-200" />
                                                )}
                                            </div>

                                            {/* CONTENT */}
                                            <div className="flex flex-col justify-start min-w-0">

                                                {/* NAME */}
                                                <div className="font-semibold text-green-800 dark:text-green-300 truncate">
                                                    {product.name}
                                                </div>

                                                {/* VARIANT */}
                                                <div className="flex items-center justify-between mt-1 w-full min-w-0">

                                                    <div className="truncate text-sm text-gray-600 dark:text-gray-300">
                                                        {gift.name}
                                                    </div>

                                                    {/* PRICE */}
                                                    <div className="text-right font-semibold text-lg text-green-700 dark:text-green-400">
                                                        {gift.base_price} {currency.symbol}
                                                    </div>
                                                </div>
                                            </div>

                                            {/* 3: DELETE BUTTON */}
                                            <div className="flex items-start justify-center">
                                                <div

                                                    className="
                w-7 h-20 rounded-lg flex items-center justify-center
            "
                                                >

                                                </div>
                                            </div>

                                            {/* 4️⃣ EXTRA SOR – KÉP ALÓL INDUL */}
                                            <div className="col-span-3 flex items-center justify-between">
                                                <div>
                                                    <div className="mt-2">
                                                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                                            Az ajándék kizárólag ezen a napon érvényes: {eventDateFormatted}
                                                        </span>
                                                        {!orderLocked && remaining && (
                                                            <div className="mt-1">
                                                                <span className="text-xs text-orange-600 dark:text-orange-400 font-medium">
                                                                    Az ajándék megszerzéséhez eddig kell leadnod a rendelést: {remaining}
                                                                </span>
                                                            </div>
                                                        )}
                                                        {orderLocked && (
                                                            <div className="mt-1">
                                                                <span className="text-xs text-green-600 dark:text-green-400 font-medium">
                                                                    Az ajándék bekerült a rendelésedbe.
                                                                </span>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>

                                                <div className="flex items-center gap-2">
                                                    <button

                                                        className="w-7 h-7 flex items-center justify-center rounded-lg 
                    
                   "
                                                    >
                                                        <span className="text-lg font-bold"></span>
                                                    </button>

                                                    {/* CURRENT QTY */}
                                                    <span className="text-sm font-medium min-w-[24px] text-center">
                                                        {giftQty} db
                                                    </span>

                                                    {/* + BUTTON */}
                                                    <button

                                                        className="w-7 h-7 flex items-center justify-center rounded-lg 
                    
                   "
                                                    >
                                                        <span className="text-lg font-bold"></span>
                                                    </button>
                                                </div>

                                            </div>

                                        </div>
                                    );
                                })}

                            </div>
                        );
                    })}

                    {!cart?.coupon && (
                        <BoxBlock
                            title={t("cart.coupon.title")}
                            icon={<Tag className="w-5 h-5 text-gray-500 dark:text-gray-300" />}
                        >
                            {/* CONTENT */}
                            <div className="space-y-4">
                                <div className="space-y-1">
                                    <label className="font-medium text-gray-700 dark:text-gray-300">
                                        {t("cart.couponcode")}
                                    </label>
                                    <input
                                        type="text"
                                        className="w-full px-3 py-2 rounded-xl border border-gray-300 dark:border-zinc-700 
                           bg-white dark:bg-zinc-900 text-gray-900 dark:text-gray-100"
                                        value={couponCode}
                                        onChange={(e) => setCouponCode(e.target.value)}
                                        placeholder="Ide add meg a kuponkódot vagy a HVG Klubkártya számát"
                                    />
                                </div>

                                <button
                                    onClick={applyCoupon}
                                    className="w-full bg-black text-white dark:bg-white dark:text-black py-3 rounded-xl font-semibold cursor-pointer"
                                >
                                    {t("cart.coupon.apply") ?? "Kupon alkalmazása"}
                                </button>
                            </div>

                        </BoxBlock>
                    )}


                    {/* ACTIVE COUPON BLOCK */}
                    {cart?.coupon && (

                        <BoxBlock
                            title={t("cart.coupon.title")}
                            icon={<Tag className="w-5 h-5 text-gray-500 dark:text-gray-300" />}
                        >
                            {/* CONTENT */}
                            <div className="">

                                {/* TOP ROW: button + basic texts */}
                                <div className="flex items-start gap-4 w-full">

                                    <button
                                        onClick={detachCoupon}
                                        onMouseEnter={() => setHoverDetach(true)}
                                        onMouseLeave={() => setHoverDetach(false)}
                                        className="
                w-10 h-10 rounded-lg flex items-center justify-center 
                bg-neutral-100 dark:bg-neutral-800
                border border-neutral-300 dark:border-neutral-700
                cursor-pointer transition
            "
                                    >
                                        {!hoverDetach ? (
                                            <>
                                                {!couponMismatch ? (
                                                    <Check className="w-5 h-5 text-green-600 dark:text-green-400" />
                                                ) : (
                                                    <Trash2 className="w-5 h-5 text-red-500 dark:text-red-400" />
                                                )}
                                            </>
                                        ) : (
                                            <Trash2 className="w-5 h-5 text-red-500 dark:text-red-400" />
                                        )}
                                    </button>

                                    <div className="flex-grow">
                                        <div className="font-semibold text-gray-900 dark:text-gray-100">
                                            {t("cart.couponcode")}: {cart.coupon.code}
                                        </div>

                                        <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                                            {cart.coupon.discount_type === "percentage" ? (
                                                `${cart.coupon.value}%`
                                            ) : (
                                                (() => {
                                                    const couponValue = cart.coupon.value;
                                                    const couponCurrency = cart.coupon.currency; // { code, symbol, rate }
                                                    const activeCurrency = currency.code;

                                                    // Ha nincs couponCurrency → hagyjuk eredeti értéken
                                                    if (!couponCurrency?.code || !couponCurrency?.rate) {
                                                        return `${formatCurrency(couponValue)} ${currency.symbol}`;
                                                    }

                                                    let finalValue = couponValue;

                                                    if (couponCurrency.code !== activeCurrency) {
                                                        finalValue = couponValue / couponCurrency.rate;
                                                    }

                                                    return `${formatCurrency(finalValue)} ${currency.symbol}`;
                                                })()
                                            )}

                                        </div>

                                        {cart.coupon.description && (
                                            <div className="text-xs mt-1 text-gray-500 dark:text-gray-400 italic">
                                                {cart.coupon.description}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* ❗ KU P O N N E M É R V É N Y E S */}
                                {couponMismatch && (
                                    <div
                                        className="
        mt-4 p-4 rounded-xl
        bg-yellow-50/70 dark:bg-yellow-900/10
        border border-yellow-200 dark:border-yellow-800
        relative w-full
        overflow-hidden
    "
                                    >
                                        {/* LEFT STRIPE */}
                                        <div className="absolute left-0 top-0 h-full w-1.5 bg-yellow-500 dark:bg-yellow-400 rounded-l-xl"></div>

                                        {/* CONTENT */}
                                        <div className="pl-4">
                                            {/* TOP ROW: icon + title */}
                                            <div className="flex items-start gap-3">
                                                <AlertTriangle className="w-5 h-5 shrink-0 text-yellow-600 dark:text-yellow-400" />
                                                <div className="font-semibold text-sm text-yellow-800 dark:text-yellow-200">
                                                    A kupon nem érvényes a kosárban lévő egyik termékre sem.
                                                </div>
                                            </div>

                                            {/* DESCRIPTION */}
                                            <div className="text-sm text-yellow-800 dark:text-yellow-300 mt-2 leading-relaxed">
                                                Választhatsz olyan terméket, amelyre érvényes, vagy továbbléphetsz -
                                                ebben az esetben a kupon nem nyújt engedményt.
                                            </div>
                                        </div>
                                    </div>


                                )}

                                {/* FULL-WIDTH CONTENT BELOW */}
                                {/* 🎯 Mely termékekre érvényes? */}
                                {Array.isArray(cart.coupon.product_variants) && cart.coupon.product_variants.length > 0 && (
                                    <div className="mt-6 w-full">
                                        <div className="flex items-center justify-between mb-3">
                                            <div className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                                                {t("cart.coupon.valid_for") ?? "Érvényes erre"}
                                            </div>

                                            <div className="text-xs text-gray-500 dark:text-gray-400">
                                                {cart.coupon.product_variants.length} variáció
                                            </div>
                                        </div>

                                        <div
                                            className="
                rounded-2xl
                border border-green-200/70 dark:border-green-800/70
                bg-gradient-to-br from-green-50/80 to-emerald-50/60
                dark:from-green-900/10 dark:to-emerald-900/10
                overflow-hidden
            "
                                        >
                                            <div className="p-4 space-y-4">
                                                {groupedCouponVariantList.map((group) => {
                                                    const visibleVariants = showAllCouponVariants
                                                        ? group.variants
                                                        : group.variants.slice(0, 4);

                                                    const hiddenCount = group.variants.length - visibleVariants.length;

                                                    return (
                                                        <div
                                                            key={group.productId}
                                                            className="
                                rounded-xl
                                bg-white/70 dark:bg-black/10
                                border border-white/60 dark:border-white/10
                                p-4
                            "
                                                        >
                                                            <div className="flex items-start justify-between gap-3">
                                                                <div className="min-w-0">
                                                                    {group.productSlug ? (
                                                                        <Link
                                                                            href={`/${group.productSlug}`}
                                                                            className="block font-semibold text-gray-900 dark:text-gray-100 hover:underline"
                                                                        >
                                                                            {group.productName}
                                                                        </Link>
                                                                    ) : (
                                                                        <div className="font-semibold text-gray-900 dark:text-gray-100">
                                                                            {group.productName}
                                                                        </div>
                                                                    )}

                                                                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                                                        {group.variants.length} érvényes variáció
                                                                    </div>
                                                                </div>
                                                            </div>

                                                            <div className="flex flex-wrap gap-2 mt-3">
                                                                {visibleVariants.map((variant) => (
                                                                    <span
                                                                        key={variant.id}
                                                                        className="
                                            inline-flex items-center
                                            px-3 py-1.5 rounded-full
                                            text-xs font-medium
                                            bg-green-100 text-green-800
                                            dark:bg-green-800/30 dark:text-green-200
                                            border border-green-200 dark:border-green-700
                                        "
                                                                    >
                                                                        {variant.name}
                                                                    </span>
                                                                ))}

                                                                {!showAllCouponVariants && hiddenCount > 0 && (
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => setShowAllCouponVariants(true)}
                                                                        className="
                                            inline-flex items-center
                                            px-3 py-1.5 rounded-full
                                            text-xs font-semibold
                                            bg-white text-green-700
                                            border border-green-200
                                            hover:bg-green-50
                                            dark:bg-transparent dark:text-green-300
                                            dark:border-green-700 dark:hover:bg-green-900/20
                                            transition
                                            cursor-pointer
                                        "
                                                                    >
                                                                        + még {hiddenCount}
                                                                    </button>
                                                                )}
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>

                                        {groupedCouponVariantList.some((group) => group.variants.length > 4) && showAllCouponVariants && (
                                            <button
                                                type="button"
                                                onClick={() => setShowAllCouponVariants(false)}
                                                className="
                    mt-3 text-sm font-medium text-green-700 dark:text-green-300
                    hover:underline cursor-pointer
                "
                                            >
                                                Kevesebb megjelenítése
                                            </button>
                                        )}
                                    </div>
                                )}
                            </div>

                        </BoxBlock>
                    )}



                    {cart?.summary && (
                        <div className="
            rounded-2xl bg-white dark:bg-zinc-900 
            shadow-md dark:border dark:border-zinc-700
            p-5 space-y-4
        ">
                            {/* HEADER */}
                            <div className="flex items-center justify-between pb-2 border-b border-gray-200 dark:border-zinc-700">
                                <span className="text-lg font-semibold text-gray-800 dark:text-gray-100">
                                    {t("cart.summary.title") ?? "Összesítés"}
                                </span>
                                <Package className="w-5 h-5 text-gray-500 dark:text-gray-300" />
                            </div>

                            {/* SUBTOTAL */}
                            <div className="flex justify-between text-gray-700 dark:text-gray-300">
                                <span>{t("cart.summary.subtotal") ?? "Termékek összesen"}</span>
                                <span className="font-semibold">
                                    {formatCurrency(cart.summary.products_subtotal_original)} {currency.symbol}
                                </span>
                            </div>

                            {/* HANDLING FEES */}
                            {cart.summary.handling_fee_total_original > 0 && (
                                <div className="flex justify-between text-gray-700 dark:text-gray-300">
                                    <span>{t("cart.summary.handling_fee") ?? "Kezelési díj"}</span>
                                    <span className="font-semibold">
                                        {formatCurrency(cart.summary.handling_fee_total_final)} {currency.symbol}
                                    </span>
                                </div>
                            )}

                            {/* DELIVERY */}
                            {cart?.summary?.shipping?.amount > 0 && (
                                <div className="flex justify-between text-gray-700 dark:text-gray-300">
                                    <span>{t("cart.summary.shipping") ?? "Kezelési díj"}</span>
                                    <span className="font-semibold">
                                        {formatCurrency(cart.summary.shipping.amount)} {currency.symbol}
                                    </span>
                                </div>
                            )}

                            {/* DISCOUNTS */}
                            {cart.summary.discount_total > 0 && (
                                <div className="flex justify-between text-red-600 dark:text-red-400">
                                    <span>{t("cart.summary.discount") ?? "Kedvezmény összesen"}</span>
                                    <span className="font-semibold">
                                        -{formatCurrency(cart.summary.discount_total)} {currency.symbol}
                                    </span>
                                </div>
                            )}

                            {/* PRICE TYPE LABEL */}
                            {/* 🟦 KOSÁRRA ÉRVÉNYES PRICE-TYPE KEDVEZMÉNYEK */}
                            {cart.summary?.price_type_per_variant && cart.summary.price_type_per_variant.length > 0 && (
                                <div className="space-y-2 mt-2">
                                    {cart.summary.price_type_per_variant
                                        .filter(pt => pt.applied_price_type) // csak ahol van kedvezmény
                                        .reduce((acc, curr) => {
                                            // ugyanazt a price-type-ot ne listázzuk többször
                                            const exists = acc.find(x => x.id === curr.applied_price_type.id);
                                            if (!exists) acc.push(curr.applied_price_type);
                                            return acc;
                                        }, [])
                                        .map((pt) => (
                                            <div
                                                key={pt.id}
                                                className="
                        px-3 py-2 rounded-xl 
                        bg-blue-50 dark:bg-blue-900/20
                        text-blue-700 dark:text-blue-300
                        text-sm font-medium
                    "
                                            >
                                                {pt.name}: {pt.discount_value}% kedvezmény
                                            </div>
                                        ))
                                    }
                                </div>
                            )}


                            {/* COUPON LABEL */}
                            {cart.summary.coupon && (
                                <div
                                    className="
    flex items-center justify-between
    px-4 py-3 rounded-xl
    bg-gradient-to-r from-green-50 to-emerald-50
    dark:from-green-900/30 dark:to-emerald-900/20
    border border-green-200 dark:border-green-800
    shadow-sm
  "
                                >

                                    {/* LEFT */}
                                    <div className="flex items-center gap-3">

                                        <div className="
        w-8 h-8 rounded-lg
        bg-green-100 dark:bg-green-800/40
        flex items-center justify-center
      ">
                                            <Tag className="w-4 h-4 text-green-600 dark:text-green-300" />
                                        </div>

                                        <div className="flex flex-col">
                                            <span className="font-semibold text-gray-900 dark:text-gray-100 text-sm">
                                                Kupon: {cart.summary.coupon.code}
                                            </span>

                                            <span className="text-xs text-gray-500 dark:text-gray-400">
                                                {cart.summary.coupon.discount_type === "percentage"
                                                    ? `${cart.summary.coupon.value}% kedvezmény`
                                                    : `${formatCurrency(cart.summary.coupon.value)} ${currency.symbol}`}
                                            </span>
                                        </div>

                                    </div>

                                    {/* RIGHT */}
                                    <div className="
      font-bold
      text-green-700 dark:text-green-300
      text-base
    ">
                                        -{formatCurrency(
                                            cart.summary.discount_total - cart.summary.cart_rule_discount_total
                                        )} {currency.symbol}
                                    </div>

                                </div>
                            )}

                            {/* CART RULE LABEL */}
                            {Array.isArray(cart.summary.applied_cart_rules) &&
                                cart.summary.applied_cart_rules.length > 0 && (
                                    <div className="space-y-3 mt-3">

                                        {cart.summary.applied_cart_rules.map((r) => {
                                            const isPercent = r.discount_percent !== null;

                                            return (
                                                <div
                                                    key={r.id}
                                                    className="
          flex items-center justify-between
          px-4 py-3 rounded-xl
          bg-gradient-to-r from-purple-50 to-indigo-50
          dark:from-purple-900/30 dark:to-indigo-900/20
          border border-purple-200 dark:border-purple-800
          shadow-sm
        "
                                                >

                                                    {/* LEFT */}
                                                    <div className="flex items-center gap-3">

                                                        <div className="
              w-8 h-8 rounded-lg
              bg-purple-100 dark:bg-purple-800/40
              flex items-center justify-center
            ">
                                                            <Tag className="w-4 h-4 text-purple-600 dark:text-purple-300" />
                                                        </div>

                                                        <div className="flex flex-col">
                                                            <span className="font-semibold text-gray-900 dark:text-gray-100 text-sm">
                                                                {r.name}
                                                            </span>

                                                            <span className="text-xs text-gray-500 dark:text-gray-400">
                                                                {isPercent
                                                                    ? `${r.discount_percent}% kedvezmény`
                                                                    : r.sets_applied > 0
                                                                        ? `${r.sets_applied} szett kedvezmény`
                                                                        : "Kosárkedvezmény"}
                                                            </span>
                                                        </div>
                                                    </div>

                                                    {/* RIGHT */}
                                                    <div className="
            font-bold
            text-purple-700 dark:text-purple-300
            text-base
          ">
                                                        -{formatCurrency(r.discount_amount)} {currency.symbol}
                                                    </div>

                                                </div>
                                            );
                                        })}
                                    </div>
                                )}


                            {/* Giftcard info */}
                            <div className="flex justify-between text-gray-700 dark:text-gray-300">
                                <span>{t("cart.giftcard.info")}</span>
                            </div>

                            {/* TOTAL */}
                            <div className="pt-3 border-t border-gray-200 dark:border-zinc-700">
                                <div className="flex justify-between items-center text-xl font-bold text-gray-900 dark:text-gray-100">
                                    <span>{t("cart.summary.total") ?? "Fizetendő"}</span>
                                    <span>
                                        {formatCurrency(cart.summary.grand_total)} {currency.symbol}
                                    </span>
                                </div>
                            </div>

                            {/* GO TO ORDER BUTTON */}
                            <Link
                                href="/order"
                                className="
                    block w-full text-center 
                    bg-black text-white dark:bg-white dark:text-black 
                    py-3 mt-3 rounded-xl font-semibold 
                    shadow-md active:scale-95 transition cursor-pointer
                "
                            >
                                {t("cart.go_to_order")}
                            </Link>
                        </div>
                    )}


                </div>
            )}
        </>
    );

    return (
        <>
            <Layout
                title="Kosár"
                description=""
                image=""
                center={centerColumn}
            >

                <ToastContainer
                    toasts={toasts}
                    removeToast={(id) => setToasts(prev => prev.filter(t => t.id !== id))}
                />

            </Layout>
        </>
    );
}

CartPage.hideFooter = true;

export default CartPage;

// 👇 ez a lényeg
async function baseGetStaticProps() {
    return {
        props: {}
    };
}

export const getStaticProps = withI18nStaticProps(baseGetStaticProps, {
    locale: "hu",
});